/**
 * ModuleSwitcherBar
 * ─────────────────
 * Persistent, additive bottom-center pill that lets the user jump directly
 * between the four core modules without going back to /modules.
 *
 * Design rules (per spec):
 *  • PURELY ADDITIVE — does not modify, wrap, or restructure any existing
 *    component, layout, or route.
 *  • Returns `null` on every route outside the four module prefixes so it
 *    never appears on auth / super-admin / settings / module-selection screens.
 *  • Uses react-router-dom's <Link> + useLocation only — no new routing logic.
 *  • Lives at z-40 → sits below any modal (z-50) but above page content,
 *    matching the convention used by AIAssistantFAB and TaskFAB.
 *  • No overlap with: AI Assistant pill (top-0 center), sticky module header
 *    (top-0 full width), or TaskFAB vertical pill (right-0 middle).
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutGrid, Target, Car, Wrench, Cog } from 'lucide-react';

const MODULES = [
  { id: 'leads',   label: 'Lead',    icon: Target, path: '/leads/list',        prefix: '/leads' },
  { id: 'sales',   label: 'Sales',   icon: Car,    path: '/sales/bookings',    prefix: '/sales' },
  { id: 'service', label: 'Service', icon: Wrench, path: '/service/dashboard', prefix: '/service' },
  { id: 'spares',  label: 'Spares',  icon: Cog,    path: '/spares',            prefix: '/spares' },
];

const isInsideModule = (pathname) =>
  MODULES.some((m) => pathname === m.prefix || pathname.startsWith(`${m.prefix}/`));

function ModuleTab({ active, label, Icon, to, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`group relative flex items-center gap-1.5 px-2.5 sm:px-3.5 py-1.5 rounded-full text-[12px] sm:text-[12.5px] font-medium transition-all
        ${active
          ? 'bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-[0_4px_10px_-2px_rgba(99,102,241,0.45)]'
          : 'text-slate-600 hover:text-violet-700 hover:bg-violet-50/70'
        }`}
    >
      <Icon size={13} className={active ? 'text-white' : 'text-slate-500 group-hover:text-violet-600'} />
      <span className="hidden xs:inline sm:inline">{label}</span>
    </Link>
  );
}

export default function ModuleSwitcherBar() {
  const { pathname } = useLocation();

  // Guard: render nothing outside the four module prefixes. This keeps the
  // bar invisible on /, /login, /modules, /admin, /super-admin, /settings/*,
  // /subscription, /whatsapp/*, /admin/* — so it never touches non-module UI.
  if (!isInsideModule(pathname)) return null;

  const activeId = MODULES.find(
    (m) => pathname === m.prefix || pathname.startsWith(`${m.prefix}/`)
  )?.id;

  return (
    <nav
      data-testid="module-switcher-bar"
      aria-label="Module Switcher"
      className="fixed bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-40
                 flex items-center gap-1 px-2 py-1.5
                 bg-white/85 backdrop-blur-xl rounded-full
                 ring-1 ring-slate-200/80
                 shadow-[0_8px_24px_-6px_rgba(15,23,42,0.18)]
                 max-w-[calc(100vw-1rem)]"
    >
      <Link
        to="/modules"
        data-testid="module-switcher-home"
        title="Back to all modules"
        className="flex items-center justify-center w-7 h-7 rounded-full text-slate-500 hover:text-violet-700 hover:bg-violet-50 transition-colors"
      >
        <LayoutGrid size={14} />
      </Link>
      <span className="w-px h-5 bg-slate-200/80" aria-hidden="true" />
      {MODULES.map((m) => (
        <ModuleTab
          key={m.id}
          active={activeId === m.id}
          label={m.label}
          Icon={m.icon}
          to={m.path}
          testId={`module-switcher-${m.id}`}
        />
      ))}
    </nav>
  );
}
